import React, { useMemo } from 'react';

import { format } from 'date-fns';

import CardLogo from 'components/candidate/CardLogo';
import ProfilePics from 'components/candidate/ProfilePics';
import { RoomAttributes } from 'lib/models/candidate-messages';
import { parseResponse } from 'lib/utils/parser';
import {
  StudentSocietyAttributes,
  StudentSocietyResponse,
} from 'lib/models/student-society';
import { CompanyAttributes, CompanyResponse } from 'lib/models/company';
import { OpportunityResponse } from 'lib/models/opportunity';
import { getDisplayName, getProcessedDate } from '../messagesUtils';
import { MessageAvatar } from '../MessageAvatar/MessageAvatar.component';

import styles from './ReceiverInfoCard.module.scss';

interface ReceiverInfoCardProps {
  room: RoomAttributes;
  showParticipants: () => void;
}

export const ReceiverInfoCard: React.FC<ReceiverInfoCardProps> = (
  props: ReceiverInfoCardProps
) => {
  const { room, showParticipants } = props;
  const {
    created_for_type,
    receiver,
    participant_count,
    touchpoint_application_applied_at: appliedDate,
  } = room;
  const { touchpointable_type, creatable_for, avatar_urls } = parseResponse(
    receiver as OpportunityResponse
  );
  const maxPicsCount = 10;

  const redirectToReceiver = (url?: string) => () => {
    const { public_url } = room.receiver.data.attributes as
      | CompanyAttributes
      | StudentSocietyAttributes;
    const redirectUrl = url || public_url;
    if (redirectUrl) {
      window.open(redirectUrl, '_blank');
    }
  };

  const displayName = useMemo(() => {
    const name = getDisplayName(room).split(' - ');
    return name[1] || name[0];
  }, [room]);

  const participants = useMemo(() => {
    return (
      <div className={styles.eventDesc} onClick={showParticipants}>
        <div className={styles.participants}>
          <ProfilePics
            maxPicsToShow={
              participant_count >= maxPicsCount
                ? maxPicsCount
                : participant_count
            }
            urls={avatar_urls || []}
          />
          {participant_count > maxPicsCount && (
            <p className={styles.count}>+ {participant_count - maxPicsCount}</p>
          )}
        </div>
        <p className={styles.participantCountSummary}>
          {participant_count} participants
        </p>
      </div>
    );
  }, [avatar_urls, participant_count]);

  const details = useMemo(() => {
    if (created_for_type === null) {
      const { address } = parseResponse<
        CompanyAttributes | StudentSocietyAttributes
      >(receiver as CompanyResponse | StudentSocietyResponse);
      return address;
    } else {
      const createdBy = parseResponse<
        CompanyAttributes | StudentSocietyAttributes
      >(creatable_for);

      if (touchpointable_type === 'Event') {
        return (
          <>
            <span className={styles.eventDetails}>
              <span className={styles.logo}>
                <CardLogo
                  is_verified={createdBy.is_verified}
                  huzzle_verified={createdBy.huzzle_verified}
                  logo_url={createdBy.icon_url}
                  initials={createdBy.name}
                  size="small"
                  cohostsLogoDetails={null}
                />
                <span onClick={redirectToReceiver(createdBy.public_url)}>
                  {createdBy.name}
                </span>
              </span>
              <span className={styles.dot}></span>
              <span>{getProcessedDate(new Date(room.created_at))}</span>
            </span>
          </>
        );
      }

      return (
        <>
          <span>{touchpointable_type}</span>
          <span className={styles.dot}></span>
          <span
            onClick={redirectToReceiver(createdBy.public_url)}
            className={styles.companyName}
          >
            {createdBy.name}
          </span>
        </>
      );
    }
  }, [room]);

  return (
    <div className={styles.receiverInfoCard}>
      <MessageAvatar size="xlarge" room={room} />
      <p className={styles.name} onClick={redirectToReceiver()}>
        {displayName}
      </p>
      {details && <p className={styles.details}>{details}</p>}
      {touchpointable_type !== 'Job' && touchpointable_type !== 'Event' && (
        <hr />
      )}
      {touchpointable_type === 'Job' && appliedDate && (
        <div className={styles.appliedOn}>
          <p>Applied on {format(new Date(appliedDate), 'MMM dd')}</p>
        </div>
      )}
      {(created_for_type === null || touchpointable_type === 'Event') && (
        <div className={styles.shortDesc}>
          {created_for_type === 'Touchpoint' && (
            <div className={styles.separator}></div>
          )}
          {touchpointable_type === 'Event' ? (
            <>{participants}</>
          ) : (
            <p>{receiver.data.attributes.short_description}</p>
          )}
        </div>
      )}
    </div>
  );
};
